import { Debt, DebtFormData } from '@/types/debt';
import { useTransactions } from '@/contexts/transactions';
import { useCategories } from '@/contexts/categories';
import { useUnifiedInstallments } from '@/contexts/unified-installments';
import { useCards } from '@/contexts/cards';
import { useProfile } from '@/contexts/profile';
import { useAuth } from '@/contexts/auth';
import { formatCurrency } from '@/utils/formatCurrency';
import { createDebt, updateDebt as updateDebtSupabase, deleteDebt as deleteDebtSupabase } from '@/lib/supabase-debts-operations';
import { logger } from '@/utils/logger';
import { v4 as uuidv4 } from 'uuid';
export const useDebtOperations = (
  debts: Debt[],
  setDebts: React.Dispatch<React.SetStateAction<Debt[]>>
) => {
  const { addTransaction, transactions } = useTransactions();
  const { getCategoryById } = useCategories();
  const { addInstallment, markInstallmentAsPaid, linkTransactionToInstallment } = useUnifiedInstallments();
  const { getCardById, getAvailableLimit } = useCards();
  const { activeProfile } = useProfile();
  const { user } = useAuth();
  
  const addDebt = async (debtData: DebtFormData): Promise<void> => {
    logger.info('[addDebt] Starting debt creation with data:', debtData);
    
    const totalAmount = debtData.calculationType === 'total' ? debtData.totalAmount! : 
      (debtData.installmentValue ? debtData.installmentValue * debtData.installments : 0);

    const newDebt: Debt = {
      id: uuidv4(),
      name: debtData.name,
      categoryId: debtData.categoryId,
      hasEntrance: debtData.hasEntrance,
      entranceAmount: debtData.hasEntrance ? debtData.entranceAmount : undefined,
      calculationType: debtData.calculationType,
      installmentValue: debtData.calculationType === 'installment' ? debtData.installmentValue : undefined,
      totalAmount,
      installments: debtData.installments,
      firstDueDate: debtData.firstDueDate,
      createdAt: new Date().toISOString(),
      description: ''
    };
    
    logger.info('[addDebt] Created debt object:', newDebt);

    // Create entrance transaction if needed
    if (debtData.hasEntrance && debtData.entranceAmount && debtData.entrancePaymentMethod) {
      try {
        logger.info('[addDebt] Creating entrance transaction');
        const entranceTransaction = await addTransaction({
          type: 'despesa' as const,
          amount: debtData.entranceAmount,
          description: `Entrada - ${debtData.name}`,
          categoryId: debtData.categoryId,
          date: debtData.entranceDate || new Date().toISOString().split('T')[0],
          paymentMethod: debtData.entrancePaymentMethod,
          cardId: debtData.entrancePaymentMethod === 'cartao' ? debtData.entranceCardId : undefined,
          accountId: debtData.entranceAccountId || '',
          account: 'Conta Principal',
          status: 'paid'
        });
        
        if (entranceTransaction && entranceTransaction.id) {
          newDebt.entranceTransactionId = entranceTransaction.id;
          logger.info('[addDebt] Entrance transaction created successfully:', entranceTransaction.id);
        }
      } catch (error) {
        logger.error('[addDebt] Error creating entrance transaction:', error);
        throw error;
      }
    }

    // Validate card limit BEFORE creating transactions (double validation layer)
    if (debtData.installmentPaymentMethod === 'cartao' && debtData.installmentCardId) {
      const card = getCardById(debtData.installmentCardId);
      if (!card) {
        logger.error('[addDebt] Card not found for installment payment');
        throw new Error('Cartão não encontrado');
      }

      const remainingAmount = totalAmount - (debtData.hasEntrance ? (debtData.entranceAmount || 0) : 0);
      const availableLimit = getAvailableLimit(debtData.installmentCardId);
      
      if (remainingAmount > availableLimit) {
        const errorMsg = `Limite insuficiente no cartão. Disponível: ${formatCurrency(availableLimit)}, Necessário: ${formatCurrency(remainingAmount)}`;
        logger.error('[addDebt] Card limit validation failed:', errorMsg);
        throw new Error(errorMsg);
      }
      
      logger.info('[addDebt] Card limit validation passed:', {
        cardName: card.name,
        availableLimit,
        requiredAmount: remainingAmount
      });
    }

    // Create installment transactions and unified installments
    const remainingAmount = totalAmount - (debtData.hasEntrance ? (debtData.entranceAmount || 0) : 0);
    const installmentAmount = debtData.calculationType === 'installment' 
      ? debtData.installmentValue! 
      : remainingAmount / debtData.installments;

    const firstDueDate = new Date(debtData.firstDueDate);

    const createdInstallments: string[] = [];
    let failedCount = 0;
    
    logger.info('[addDebt] Creating installment transactions:', {
      installments: debtData.installments,
      installmentAmount,
      paymentMethod: debtData.installmentPaymentMethod,
      cardId: debtData.installmentCardId,
      accountId: debtData.installmentAccountId
    });

    for (let i = 1; i <= debtData.installments; i++) {
      const dueDate = new Date(firstDueDate);
      dueDate.setMonth(dueDate.getMonth() + (i - 1));
      const dueDateStr = dueDate.toISOString().split('T')[0];
      
      try {
        logger.debug(`[addDebt] Creating installment ${i}/${debtData.installments}`);
        
        // Create unified installment first
        const unifiedInstallmentId = addInstallment({
          parentId: newDebt.id,
          parentType: 'debt',
          number: i,
          amount: installmentAmount,
          dueDate: dueDateStr,
          status: 'pending',
          description: `${debtData.name} - Parcela ${i}/${debtData.installments}`
        });
        
        logger.debug(`[addDebt] Unified installment ${i} created:`, unifiedInstallmentId);
        
        // Create transaction for the installment
        const installmentTransaction = await addTransaction({
          type: 'despesa' as const,
          amount: installmentAmount,
          description: `${debtData.name} - Parcela ${i}/${debtData.installments}`,
          categoryId: debtData.categoryId,
          date: dueDateStr,
          paymentMethod: debtData.installmentPaymentMethod || 'pix',
          cardId: debtData.installmentPaymentMethod === 'cartao' ? debtData.installmentCardId : undefined,
          accountId: debtData.installmentAccountId || '',
          account: debtData.installmentAccountId ? 'Conta Selecionada' : 'Pix',
          status: 'pending',
          installmentNumber: i,
          installments: debtData.installments
        });

        if (installmentTransaction && installmentTransaction.id) {
          createdInstallments.push(installmentTransaction.id);
          
          // Link the transaction to the unified installment
          linkTransactionToInstallment(unifiedInstallmentId, installmentTransaction.id);
          
          logger.debug(`[addDebt] Installment ${i} transaction created and linked:`, {
            installmentId: unifiedInstallmentId,
            transactionId: installmentTransaction.id
          });
        }

      } catch (error) {
        failedCount++;
        logger.error(`[addDebt] Error creating installment ${i}:`, error);
        
        if (failedCount > debtData.installments / 2) {
          logger.error('[addDebt] Too many installment failures, aborting debt creation');
          throw new Error(`Falha ao criar parcelas da dívida. ${failedCount} de ${debtData.installments} falharam.`);
        }
      }
    }
    
    logger.info(`[addDebt] Installment creation completed: ${createdInstallments.length} successful, ${failedCount} failed`);

    // Save to Supabase
    if (user && activeProfile) {
      try {
        await createDebt(newDebt, user.id, activeProfile.id);
        logger.info('[addDebt] Debt saved to Supabase successfully');
      } catch (error) {
        logger.error('[addDebt] Error saving debt to Supabase:', error);
        throw new Error('Erro ao salvar dívida no servidor');
      }
    }

    setDebts(prev => [...prev, newDebt]);
    logger.info('[addDebt] Debt creation completed successfully:', newDebt.id);
    
    // Dispatch event to reload transactions
    window.dispatchEvent(new CustomEvent('debt-transactions-created', { 
      detail: { debtId: newDebt.id, transactionCount: createdInstallments.length } 
    }));
  };

  const updateDebt = async (id: string, debtData: Partial<Debt>) => {
    if (user && activeProfile) {
      try {
        await updateDebtSupabase(id, debtData, user.id);
        logger.info('[updateDebt] Debt updated in Supabase successfully');
      } catch (error) {
        logger.error('[updateDebt] Error updating debt in Supabase:', error);
        throw new Error('Erro ao atualizar dívida no servidor');
      }
    }
    
    setDebts(prev => prev.map(debt => debt.id === id ? { ...debt, ...debtData } : debt));
  };

  const deleteDebt = async (id: string) => {
    if (user && activeProfile) {
      try {
        await deleteDebtSupabase(id, user.id);
        logger.info('[deleteDebt] Debt deleted from Supabase successfully');
      } catch (error) {
        logger.error('[deleteDebt] Error deleting debt from Supabase:', error);
        throw new Error('Erro ao excluir dívida no servidor');
      }
    }
    
    setDebts(prev => prev.filter(debt => debt.id !== id));
  };

  // NEW: Get only the entrance transaction for a debt
  const getDebtEntranceTransaction = (debtId: string) => {
    const debt = debts.find(d => d.id === debtId);
    if (!debt || !debt.hasEntrance) return null;
    
    return transactions.find(transaction => 
      transaction.description === `Entrada - ${debt.name}` && 
      transaction.type === 'despesa' &&
      !transaction.deleted
    ) || null;
  };

  // NEW: Get only the installment transactions for a debt
  const getDebtInstallmentTransactions = (debtId: string) => {
    const debt = debts.find(d => d.id === debtId);
    if (!debt) return [];
    
    return transactions.filter(transaction => 
      transaction.description?.includes(`${debt.name} - Parcela`) &&
      transaction.description?.match(/Parcela \d+\/\d+/) &&
      transaction.type === 'despesa' &&
      !transaction.deleted
    );
  };

  // IMPROVED: Enhanced version that can return both or specific types
  const getDebtTransactions = (debtId: string, type?: 'entrance' | 'installments' | 'all') => {
    if (type === 'entrance') {
      const entrance = getDebtEntranceTransaction(debtId);
      return entrance ? [entrance] : [];
    }
    
    if (type === 'installments') {
      return getDebtInstallmentTransactions(debtId);
    }
    
    // Default behavior: return all (entrance + installments)
    const entrance = getDebtEntranceTransaction(debtId);
    const installments = getDebtInstallmentTransactions(debtId);
    
    return [
      ...(entrance ? [entrance] : []),
      ...installments
    ];
  };

  return {
    addDebt,
    updateDebt,
    deleteDebt,
    getDebtTransactions,
    getDebtEntranceTransaction,
    getDebtInstallmentTransactions
  };
};
